import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { Menu, Switch, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { SidebarContext } from '../../context/SidebarContext';
import { useLogout } from '../../hooks/useLogout';

/**
 * The account card in the topbar, and its dropdown.
 *
 * Replaces the right sidebar's profile block for B2B admins and Shoorah staff
 * — same destinations, a fraction of the space. Menu entries are data, so
 * adding one is a line in the item list rather than more JSX.
 *
 * It is a card rather than a bare avatar because who you are signed in as
 * matters in this panel: a super admin and a company admin see different data
 * under the same URLs, and an initials circle does not say which you are. The
 * name and role collapse away below `xl`, where the topbar has titles and
 * filters competing for the same row and the avatar alone has to do.
 *
 * The two audiences need different entries: a company admin's pages live
 * under their company slug, while Shoorah's own super and sub admins have no
 * company and no subscription, so their menu points at the platform-level
 * pages instead.
 */

const initialsOf = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.charAt(0) || '') + (parts[1]?.charAt(0) || '')).toUpperCase();
};

const ProfileMenu = ({ userData, companyLogo, subscriptionLabel }) => {
  const { isDarkModeEnabled, setIsDarkModeEnabled } = useContext(SidebarContext);
  const slug = userData?.slug;

  // userType 0/1 are Shoorah's own super and sub admins; 3/4 are a company's.
  const isShoorahStaff = userData?.userType === 0 || userData?.userType === 1;

  const roleLabel =
    userData?.userType === 0 || userData?.userType === 3 ? 'Super Admin' : 'Sub Admin';

  const items = isShoorahStaff
    ? [
        { label: 'Shoorah Admins', to: '/sub-admins' },
        { label: 'Change Password', to: '/change-password' }
      ]
    : [
        { label: 'My Company', to: `/${slug}/myCompany` },
        { label: 'Shoorah Admins', to: `/${slug}/sub-admins` },
        { label: 'Change Password', to: `/${slug}/change-password` }
      ];

  const { requestLogout, logoutConfirm } = useLogout();

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center gap-2 xl:gap-3 rounded-2xl border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder bg-white dark:bg-shoorah-darkBgTabColor p-1 xl:ps-2 xl:pe-3 xl:py-2 text-left transition-colors hover:bg-shoorah-normanGray dark:hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active">
        {companyLogo && !companyLogo.includes('null') ? (
          <img
            loading="lazy"
            src={companyLogo}
            alt=""
            className="w-8 h-8 xl:w-10 xl:h-10 rounded-full object-cover shrink-0"
          />
        ) : (
          <span className="w-8 h-8 xl:w-10 xl:h-10 rounded-full bg-shoorah-sidebarBackground dark:bg-shoorah-darkBgColor flex items-center justify-center text-white text-xs xl:text-sm font-medium shrink-0">
            {initialsOf(userData?.name)}
          </span>
        )}

        {/* The name is the point of the card, but it is the one part that can
            be any length, so it truncates rather than widening the topbar. */}
        <span className="min-w-0 hidden xl:block">
          <span className="block text-sm font-semibold leading-tight text-shoorah-lightBlack dark:text-white truncate max-w-[140px]">
            {userData?.name}
          </span>
          <span className="block text-xs leading-tight text-shoorah-gray3 truncate">
            {roleLabel}
          </span>
        </span>

        <span className="hidden xl:flex items-center justify-center w-6 h-6 rounded-full border border-shoorah-borderGray dark:border-shoorah-darkBorder text-shoorah-gray3 shrink-0">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="w-3.5 h-3.5">
            <path
              d="M5.5 7.75 10 12.25l4.5-4.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-60 max-w-[calc(100vw-2rem)] origin-top-right rounded-2xl bg-white dark:bg-shoorah-darkBgTabColor border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder shadow-lg focus:outline-none z-[100] overflow-hidden">
          {/* identity header — what the right sidebar's top card carried */}
          <div className="px-4 py-3 border-b border-shoorah-borderGray dark:border-shoorah-darkBorder">
            <p className="text-sm font-semibold text-shoorah-lightBlack dark:text-shoorah-darkBg truncate">
              {userData?.name}
            </p>
            <p className="text-xs text-shoorah-gray3">{roleLabel}</p>
          </div>

          {/* subscription status — informational, as it was in the sidebar.
              Shoorah staff have no subscription, so the row is omitted rather
              than showing "No subscription found" to the platform's owners. */}
          {!isShoorahStaff && (
            <div className="px-4 py-3 border-b border-shoorah-borderGray dark:border-shoorah-darkBorder">
              <p className="text-[11px] uppercase tracking-wider text-shoorah-gray3 font-medium mb-[2px]">
                Subscription
              </p>
              <p className="text-xs text-shoorah-lightBlack dark:text-shoorah-darkBg">
                {subscriptionLabel}
              </p>
            </div>
          )}

          <div className="py-1">
            {items.map((item) => (
              <Menu.Item key={item.label}>
                {({ active }) => (
                  <Link
                    to={item.to}
                    className={`block px-4 py-2 text-sm whitespace-nowrap ${
                      active
                        ? 'bg-shoorah-normanGray dark:bg-shoorah-darkBgColor text-shoorah-lightBlack dark:text-shoorah-darkBg'
                        : 'text-shoorah-gray4 dark:text-shoorah-darkBg'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </Menu.Item>
            ))}
          </div>

          <div className="py-1 border-t border-shoorah-borderGray dark:border-shoorah-darkBorder">
            <div
              className="flex items-center justify-between px-4 py-2 text-sm text-shoorah-gray4 dark:text-shoorah-darkBg cursor-pointer"
              onClick={(event) => {
                // The menu closes on click by default; dark mode is a setting
                // you may want to flip and look at, not a destination.
                event.preventDefault();
                event.stopPropagation();
                setIsDarkModeEnabled(!isDarkModeEnabled);
              }}
            >
              <span>Dark mode</span>
              <Switch
                checked={isDarkModeEnabled}
                onChange={() => setIsDarkModeEnabled(!isDarkModeEnabled)}
                className="relative border-shoorah-lightBlack dark:border-shoorah-darkBg inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 duration-200 ease-in-out focus:outline-none"
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-shoorah-lightBlack dark:bg-shoorah-darkBg shadow ring-0 transition duration-200 ease-in-out ${
                    isDarkModeEnabled ? 'translate-x-3' : 'translate-x-0'
                  }`}
                />
              </Switch>
            </div>
          </div>

          <div className="py-1 border-t border-shoorah-borderGray dark:border-shoorah-darkBorder">
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={requestLogout}
                  className={`block w-full text-left px-4 py-2 text-sm ${
                    active
                      ? 'bg-shoorah-normanGray dark:bg-shoorah-darkBgColor text-shoorah-darkRed'
                      : 'text-shoorah-darkRed'
                  }`}
                >
                  Log out
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>

      {/* Outside Menu.Items: the menu unmounts its panel on select, which
          would take the confirmation down with it before it was answered. */}
      {logoutConfirm}
    </Menu>
  );
};

ProfileMenu.propTypes = {
  userData: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    userType: PropTypes.number
  }),
  companyLogo: PropTypes.string,
  subscriptionLabel: PropTypes.string
};

ProfileMenu.defaultProps = {
  subscriptionLabel: 'No subscription found'
};

export default ProfileMenu;
